import { useAuth } from '@berky/auth'
import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorState,
  Input,
  Label,
  LoadingSkeleton,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { Navigate } from 'react-router'

import { roleAccess } from '../module-access'
import { modules } from '../modules'
import { PLATFORM_ADMIN_ROLE } from '../verwaltung/api'
import { usePlatformUsageApi, type UsageSettings } from './api'
import { UsageAdoption } from './usage-adoption'
import { UsageDetails } from './usage-details'
import { UsageOverview } from './usage-overview'
import {
  berlinDate,
  initialFilters,
  shiftDate,
  validateFilters,
  type UsageFilters,
} from './usage-utils'

/** Historical analysis: no presence state or automatically refreshing live report. */
export function UsagePage() {
  const { user, isLoading } = useAuth()
  const access = roleAccess(user?.roles, [PLATFORM_ADMIN_ROLE])
  const api = usePlatformUsageApi()
  const client = useQueryClient()
  const [filters, setFilters] = useState(initialFilters)
  const [draft, setDraft] = useState(initialFilters)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState('activity')
  const settings = useQuery({
    queryKey: ['platform-usage-settings'],
    queryFn: ({ signal }) => api.get<UsageSettings>('/settings', { signal }),
    enabled: access === 'erlaubt',
    refetchOnWindowFocus: true,
  })
  const changeMode = useMutation({
    mutationFn: (testMode: boolean) =>
      api.put<UsageSettings>('/settings', {
        testMode,
        version: settings.data?.version,
      }),
    onSuccess: async (policy) => {
      setTab('activity')
      await client.cancelQueries({ queryKey: ['platform-usage'] })
      client.removeQueries({ queryKey: ['platform-usage'] })
      client.setQueryData(['platform-usage-settings'], policy)
      toast.success(
        policy.testMode
          ? 'Detaillierte Analyse aktiviert'
          : 'Detaillierte Analyse deaktiviert',
      )
    },
    onError: () => {
      toast.error(
        'Die Einstellung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
      )
      void settings.refetch()
    },
  })

  if (isLoading || access === 'unbekannt')
    return <LoadingSkeleton variant="text" rows={6} />
  if (access !== 'erlaubt')
    return <Navigate to="/forbidden" replace state={{ moduleTitle: 'Nutzungsanalyse' }} />

  function apply(next: UsageFilters) {
    const problem = validateFilters(next)
    setError(problem)
    if (problem) return
    setFilters(next)
    setDraft(next)
  }

  const testMode = settings.data?.testMode === true
  const filterKey = `${filters.from}:${filters.to}:${filters.moduleId}`
  return (
    <div className="space-y-6">
      <PageHeader
        title="Nutzungsanalyse"
        description="Verstehen Sie, wie Berky Digital angenommen wird und welche Bereiche genutzt werden."
        actions={
          <Badge variant="secondary">
            <CalendarDays aria-hidden="true" />
            Historische Auswertung
          </Badge>
        }
      />

      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" aria-label="Zeitraum wählen">
              {[
                { label: 'Heute', days: 1 },
                { label: '7 Tage', days: 7 },
                { label: '30 Tage', days: 30 },
                { label: '90 Tage', days: 90 },
              ].map(({ label, days }) => {
                const to = berlinDate()
                const from = shiftDate(to, 1 - days)
                const active = filters.from === from && filters.to === to
                return (
                  <Button
                    key={label}
                    size="sm"
                    variant={active ? 'default' : 'outline'}
                    aria-pressed={active}
                    onClick={() => apply({ ...filters, from, to })}
                  >
                    {label}
                  </Button>
                )
              })}
            </div>
            <Button size="sm" variant="ghost" onClick={() => apply(initialFilters())}>
              <RotateCcw aria-hidden="true" />
              Zurücksetzen
            </Button>
          </div>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              apply(draft)
            }}
          >
            <div className="grid gap-1.5">
              <Label htmlFor="usage-from">Von</Label>
              <Input
                id="usage-from"
                type="date"
                min="2000-01-01"
                max={berlinDate()}
                required
                value={draft.from}
                aria-invalid={!!error}
                aria-describedby={error ? 'usage-filter-error' : undefined}
                onChange={(event) => setDraft({ ...draft, from: event.target.value })}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="usage-to">Bis</Label>
              <Input
                id="usage-to"
                type="date"
                min="2000-01-01"
                max={berlinDate()}
                required
                value={draft.to}
                aria-invalid={!!error}
                aria-describedby={error ? 'usage-filter-error' : undefined}
                onChange={(event) => setDraft({ ...draft, to: event.target.value })}
              />
            </div>
            <div className="grid min-w-48 flex-1 gap-1.5">
              <Label htmlFor="usage-module">Bereich</Label>
              <Select
                value={draft.moduleId || '__all__'}
                onValueChange={(value) =>
                  setDraft({ ...draft, moduleId: value === '__all__' ? '' : value })
                }
              >
                <SelectTrigger id="usage-module" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Berky Digital insgesamt</SelectItem>
                  <SelectItem value="platform">
                    Startseite & Plattformfunktionen
                  </SelectItem>
                  {modules.map((module) => (
                    <SelectItem key={module.id} value={module.id}>
                      {module.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit">Anwenden</Button>
          </form>
          {error && (
            <p id="usage-filter-error" role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </CardContent>
      </Card>

      {settings.isPending ? (
        <LoadingSkeleton variant="text" rows={3} />
      ) : settings.isError ? (
        <ErrorState
          description="Die Einstellungen der Nutzungsanalyse konnten nicht geladen werden."
          onRetry={() => void settings.refetch()}
        />
      ) : (
        <>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4 flex h-auto flex-wrap justify-start">
              <TabsTrigger value="activity">Nutzeraktivität</TabsTrigger>
              <TabsTrigger value="overview">Module & Trends</TabsTrigger>
              <TabsTrigger value="details">Detailliste</TabsTrigger>
            </TabsList>
            <TabsContent value="activity">
              <UsageAdoption key={filterKey} filters={filters} />
            </TabsContent>
            <TabsContent value="overview">
              <UsageOverview
                key={filterKey}
                filters={filters}
                settings={settings.data!}
                onFilter={apply}
              />
            </TabsContent>
            <TabsContent value="details">
              <UsageDetails key={filterKey} filters={filters} />
            </TabsContent>
          </Tabs>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border bg-muted/30 p-4">
            <div className="max-w-3xl space-y-1">
              <Label htmlFor="usage-test-mode" className="text-base">
                Detaillierte Analyse {testMode ? 'aktiviert' : 'deaktiviert'}
              </Label>
              <p id="usage-policy-description" className="text-sm text-muted-foreground">
                {testMode
                  ? 'Unterseiten und geschätzte aktive Zeit werden zusätzlich erfasst.'
                  : 'Unterseiten und geschätzte aktive Zeit werden nicht erfasst.'}{' '}
                Modulzugriffe und gemeldete fachliche Aktivität bleiben für die
                Nutzeraktivität verfügbar. Bestehende Detailverläufe werden nicht
                gelöscht.
              </p>
            </div>
            <Switch
              id="usage-test-mode"
              checked={testMode}
              disabled={changeMode.isPending}
              aria-describedby="usage-policy-description"
              onCheckedChange={(checked) => changeMode.mutate(checked)}
            />
          </div>
        </>
      )}
    </div>
  )
}
